import './styles.css';
import { AuthProviders, configureAuth } from '@packages/cognito-auth/react';
import { type AppName, posthogProjects } from '@packages/shared';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppRouter } from '@/AppRouter';
import { ThemeProvider } from '@/components/ThemeProvider';
import { AppVersionGuard } from '@/features/app-updates/AppVersionGuard';
import { DEV_AUTH_ENABLED } from '@/features/auth/devAuth';
import { convexUrl, env } from '@/lib/env';

const stage = env.VITE_STAGE;
// Square sellers sign in with Square, through Cognito's hosted UI, and come back here
configureAuth({
  stage,
  devAuth: DEV_AUTH_ENABLED,
  oauth: { redirectUrl: `${window.location.origin}/` },
});

// Set the page <title>, based on STAGE.
document.title = stage === 'production' ? 'Admin' : `Admin *${stage}`;

// Setup PostHog: the stage's project, shared with the native apps.
posthog.init(posthogProjects[stage].writeKey, {
  api_host: 'https://us.i.posthog.com',
  defaults: '2025-05-24',
});
// On every event, like the native apps' `customAppProperties`. CI deploys set the run number;
// dev, e2e and preview builds have none.
posthog.register({
  app: 'admin-web' satisfies AppName,
  $app_version: env.VITE_GITHUB_RUN_NUMBER ?? 'local',
});
// Super properties persist across page loads. The restaurant layout registers `restaurant_id`
// while it's open, so drop one left over from an earlier load.
posthog.unregister('restaurant_id');

// biome-ignore lint/style/noNonNullAssertion: root needs to exist for app to render
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <AuthProviders convexUrl={convexUrl}>
        <ThemeProvider>
          <AppVersionGuard>
            <AppRouter />
          </AppVersionGuard>
        </ThemeProvider>
      </AuthProviders>
    </PostHogProvider>
  </StrictMode>,
);
